/**
 * Hotels: along the route ahead, and around the destination.
 *
 * Two tabs because they answer different questions. "Near the destination" is
 * the plan you make before setting off; "along the route" is the one you make
 * at 21:00 when you are not going to finish tonight. A single merged list
 * would bury whichever of those the driver actually wanted.
 *
 * NO PRICES - see nav/hotels.ts for why (every live-rate API needs a
 * commercial partnership this fleet cannot get). Each row offers two actions:
 * navigate there, or open Agoda for what a room costs tonight. The price
 * question is answered by handing it to someone who can answer it, which is
 * honest, free, and cannot go stale.
 *
 * The list is fetched on OPEN, never on a timer. A hotel search is four
 * category calls per probe point and the answer does not change minute to
 * minute; polling it would burn the car's connection for nothing.
 */

import type { Hotel } from '../nav/hotels';
import { bookingUrl } from '../nav/hotels';
import type { UnitSystem } from '../core/settings';
import { formatDistance } from '../core/format';
import { t } from '../core/i18n';

export type HotelTab = 'route' | 'destination' | 'here';

export interface HotelSheetState {
  /** Null while the search is in flight. */
  route: Hotel[] | null;
  destination: Hotel[] | null;
  /** Around the current GPS fix - "I want to stop, what is right here". */
  here: Hotel[] | null;
  /** False with no fix, so the tab explains itself rather than showing empty. */
  hasFix: boolean;
  units: UnitSystem;
  /** Shown as the destination tab's heading. */
  destinationName: string | null;
  /** True while navigating - the route tab leads then, not the destination. */
  navigating: boolean;
  error: string | null;
}

export class HotelSheet {
  readonly button: HTMLElement;
  private sheet: HTMLElement;
  private tab: HotelTab = 'destination';

  constructor(
    parent: HTMLElement,
    private get: () => HotelSheetState,
    private actions: {
      /** Fetch for a tab; called on open and on tab switch. */
      onNeed: (tab: HotelTab) => void;
      /** Set this hotel as the destination. */
      onNavigate: (hotel: Hotel) => void;
    },
  ) {
    this.button = document.createElement('button');
    this.button.className = 'hotel-fab';
    this.button.hidden = true;
    this.button.setAttribute('aria-label', t('hotel.title'));
    this.button.innerHTML = BED_SVG;
    parent.appendChild(this.button);
    this.button.addEventListener('click', () => this.open());

    this.sheet = document.createElement('div');
    this.sheet.className = 'hotel-sheet';
    this.sheet.hidden = true;
    parent.appendChild(this.sheet);
  }

  setVisible(visible: boolean): void {
    this.button.hidden = !visible;
    if (!visible) this.close();
  }

  /** True while the sheet is showing - used by the hardware-Back handler. */
  isOpen(): boolean {
    return !this.sheet.hidden;
  }

  close(): void {
    this.sheet.hidden = true;
    this.sheet.innerHTML = '';
  }

  open(): void {
    // Mid-drive the useful list is what is coming up; from the preview it is
    // where you are going.
    this.tab = this.get().navigating ? 'route' : 'destination';
    this.sheet.hidden = false;
    this.render();
    this.actions.onNeed(this.tab);
  }

  /** Redraw only when open - the caller pushes results in as they land. */
  refresh(): void {
    if (!this.sheet.hidden) this.render();
  }

  private render(): void {
    const s = this.get();
    const list =
      this.tab === 'route' ? s.route : this.tab === 'here' ? s.here : s.destination;

    this.sheet.innerHTML = `
      <div class="hotel-sheet__card">
        <div class="hotel-sheet__head">
          <h3>${t('hotel.title')}</h3>
          <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">
            ${X_SVG}
          </button>
        </div>

        <div class="hotel-sheet__tabs" role="tablist">
          <button class="hotel-tab ${this.tab === 'route' ? 'is-on' : ''}"
                  data-tab="route" role="tab" aria-selected="${this.tab === 'route'}">
            ${t('hotel.tabRoute')}
          </button>
          <button class="hotel-tab ${this.tab === 'here' ? 'is-on' : ''}"
                  data-tab="here" role="tab" aria-selected="${this.tab === 'here'}">
            ${t('hotel.tabHere')}
          </button>
          <button class="hotel-tab ${this.tab === 'destination' ? 'is-on' : ''}"
                  data-tab="destination" role="tab" aria-selected="${this.tab === 'destination'}">
            ${escapeHtml(s.destinationName || t('hotel.tabDest'))}
          </button>
        </div>

        <div class="hotel-sheet__scroll">
          ${
            s.error
              ? `<p class="hotel-sheet__empty">${escapeHtml(s.error)}</p>`
              : list === null
                ? `<p class="hotel-sheet__empty">${t('hotel.loading')}</p>`
                : !list.length
                  ? `<p class="hotel-sheet__empty">${
                      this.tab === 'here'
                        ? // No fix is a different problem from no hotels, and
                          // saying "none nearby" when we simply do not know
                          // where we are would be a lie.
                          s.hasFix
                          ? t('hotel.noneHere')
                          : t('hotel.noFix')
                        : this.tab === 'route'
                          ? t('hotel.noneRoute')
                          : t('hotel.noneDest')
                    }</p>`
                  : list.map((h) => this.row(h, s.units)).join('')
          }
        </div>
      </div>`;

    this.wire();
  }

  private row(h: Hotel, units: UnitSystem): string {
    // Along-route hotels are placed by how far ahead they are; destination
    // hotels by how far from the destination. Those are different facts and
    // labelling them the same way would mislead.
    const where =
      h.along !== null
        ? t('hotel.ahead', { d: formatDistance(h.along, units) })
        : t('hotel.fromDest', { d: formatDistance(h.fromDestination ?? 0, units) });

    const side =
      h.side && h.offset > 40
        ? ` · ${h.side === 'left' ? t('hotel.left') : t('hotel.right')}`
        : '';

    return `
      <div class="hotel-row">
        <div class="hotel-row__main">
          <div class="hotel-row__name">
            ${escapeHtml(h.name)}
            <span class="hotel-kind hotel-kind--${h.kind}">${
              h.kind === 'resort' ? t('hotel.kindResort') : t('hotel.kindHotel')
            }</span>
          </div>
          <div class="hotel-row__meta">${escapeHtml(where)}${side}</div>
          ${
            // The Thai name under the romanisation: the romanised form is for
            // reading, the Thai is what is on the sign and what a local will
            // recognise if the driver has to stop and ask.
            h.nameOriginal
              ? `<div class="hotel-row__thai">${escapeHtml(h.nameOriginal)}</div>`
              : h.address
                ? `<div class="hotel-row__addr">${escapeHtml(h.address)}</div>`
                : ''
          }
        </div>
        <div class="hotel-row__actions">
          ${
            h.phone
              ? `<a class="icon-btn hotel-row__call" href="tel:${escapeAttr(h.phone)}"
                    aria-label="${t('hotel.call')}">${PHONE_SVG}</a>`
              : ''
          }
          <button class="btn btn--ghost btn--small" data-act="hotel-prices" data-id="${escapeAttr(h.id)}">
            ${t('hotel.prices')}
          </button>
          <button class="btn btn--primary btn--small" data-act="hotel-go" data-id="${escapeAttr(h.id)}">
            ${t('hotel.go')}
          </button>
        </div>
      </div>`;
  }

  private wire(): void {
    this.sheet.querySelector('[data-act=close]')?.addEventListener('click', () => this.close());

    this.sheet.querySelectorAll<HTMLElement>('[data-tab]').forEach((b) =>
      b.addEventListener('click', () => {
        const next = b.dataset.tab as HotelTab;
        if (next === this.tab) return;
        this.tab = next;
        this.render();
        this.actions.onNeed(next);
      }),
    );

    const find = (id: string): Hotel | undefined => {
      const s = this.get();
      const list =
      this.tab === 'route' ? s.route : this.tab === 'here' ? s.here : s.destination;
      return list?.find((h) => h.id === id);
    };

    this.sheet.querySelectorAll<HTMLElement>('[data-act=hotel-go]').forEach((b) =>
      b.addEventListener('click', () => {
        const h = find(b.dataset.id ?? '');
        if (!h) return;
        this.actions.onNavigate(h);
        this.close();
      }),
    );

    this.sheet.querySelectorAll<HTMLElement>('[data-act=hotel-prices]').forEach((b) =>
      b.addEventListener('click', () => {
        const h = find(b.dataset.id ?? '');
        if (!h) return;
        /*
         * A new tab, not this one. On the head unit the app is the navigation;
         * replacing it with a booking site mid-journey would be the wrong kind
         * of surprise, and coming back would mean a cold reload.
         */
        window.open(bookingUrl(h), '_blank', 'noopener');
      }),
    );
  }
}

const BED_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7 v11 M3 12 h18 v6 M21 18 v-4.5 a2 2 0 0 0 -2 -2 H10.5 V9 a2 2 0 0 0 -2 -2 H3" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7" cy="10.5" r="1.6" fill="currentColor"/></svg>`;
const X_SVG = `<svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
const PHONE_SVG = `<svg viewBox="0 0 24 24"><path d="M6.5 3.5 h3 l1.5 4 -2 1.5 a11 11 0 0 0 6 6 l1.5 -2 4 1.5 v3 a2 2 0 0 1 -2 2 A16 16 0 0 1 4.5 5.5 a2 2 0 0 1 2 -2 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Same escaping, named for use inside a quoted attribute. */
const escapeAttr = escapeHtml;
